import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize, forkJoin, of } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import {
  EquipmentResource, EquipmentUsage, FieldEquipmentAllocation, FieldLaborAssignment, FieldLaborWorkEntry,
  FieldWorkAction, FieldWorkPackage
} from './field-work.models';
import { FieldWorkService } from './field-work.service';

type CustodyForm = FieldWorkAction & { issueCondition?: string; returnCondition?: string };

@Component({
  selector: 'app-field-work-execution-dialog',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ButtonModule, DatePickerModule, DialogModule,
    InputNumberModule, InputTextModule, IftaLabelModule, InputGroupModule, InputGroupAddonModule,
    SelectModule, TagModule, TextareaModule
  ],
  templateUrl: './field-work-execution-dialog.component.html',
  styleUrls: ['./field-work-execution-dialog.component.css']
})
export class FieldWorkExecutionDialogComponent implements OnChanges {
  private readonly service = inject(FieldWorkService);

  @Input() visible = false;
  @Input() work?: FieldWorkPackage;
  @Input() canExecute = false;
  @Input() canApprove = false;
  @Input() canCheckout = false;
  @Input() canRelease = false;
  @Input() canViewEquipment = false;
  @Input() canViewRates = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() changed = new EventEmitter<FieldWorkPackage>();

  readonly currentWork = signal<FieldWorkPackage | undefined>(undefined);
  readonly laborEntries = signal<FieldLaborWorkEntry[]>([]);
  readonly equipmentUsages = signal<EquipmentUsage[]>([]);
  readonly loading = signal(false);
  readonly actionKey = signal('');
  readonly error = signal('');
  readonly message = signal('');
  readonly evidenceUploading = signal(new Set<string>());

  laborForms: Record<number, FieldLaborWorkEntry> = {};
  laborDates: Record<number, Date> = {};
  equipmentForms: Record<number, EquipmentUsage> = {};
  equipmentDates: Record<number, Date> = {};
  custodyForms: Record<number, CustodyForm> = {};
  decisionReasons: Record<string, string> = {};
  substitutionCandidates: Record<number, EquipmentResource[]> = {};
  substitutionResourceIds: Record<number, number | undefined> = {};
  substitutionReasons: Record<number, string> = {};
  evidenceFileNames: Record<string, string> = {};

  readonly conditions = [
    { label: 'Good', value: 'GOOD' },
    { label: 'Serviceable', value: 'SERVICEABLE' },
    { label: 'Damaged', value: 'DAMAGED' },
    { label: 'Lost', value: 'LOST' }
  ];

  readonly laborProgress = computed(() => {
    const assignments = this.currentWork()?.laborAssignments || [];
    const recorded = assignments.filter(assignment => Boolean(this.entryFor(assignment.fieldLaborAssignmentId))).length;
    const submitted = assignments.filter(assignment =>
      ['SUBMITTED', 'APPROVED', 'REVERSED'].includes(this.entryFor(assignment.fieldLaborAssignmentId)?.entryStatus || '')).length;
    return { recorded, submitted, total: assignments.length };
  });

  readonly equipmentProgress = computed(() => {
    const allocations = this.currentWork()?.equipmentAllocations || [];
    const returned = allocations.filter(allocation => allocation.allocationStatus === 'RETURNED').length;
    const usageRecorded = allocations.filter(allocation => Boolean(this.usageFor(allocation.fieldEquipmentAllocationId))).length;
    return { returned, usageRecorded, total: allocations.length };
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['work'] || changes['visible']?.currentValue === true) {
      this.currentWork.set(this.work);
      this.error.set('');
      this.message.set('');
      this.resetForms();
    }
    if (changes['visible']?.currentValue === true) this.refresh();
  }

  get status(): string {
    return this.currentWork()?.status || 'DRAFT';
  }

  get nextActionTitle(): string {
    if (this.status === 'RELEASED') return 'Start field execution';
    if (this.hasUnissuedEquipment()) return 'Issue reserved equipment';
    if (this.hasMissingActuals()) return 'Record actual work and usage';
    if (this.hasDraftActuals()) return 'Submit actuals for approval';
    if (this.hasSubmittedActuals()) return 'Supervisor approval is pending';
    if (this.status === 'IN_PROGRESS') return 'Return equipment and complete work';
    return 'Execution record is complete';
  }

  get nextActionText(): string {
    if (this.status === 'RELEASED') {
      return 'Start the package when the field team begins. Equipment can then be issued to a named custodian.';
    }
    if (this.hasUnissuedEquipment()) {
      return 'Record outgoing condition and optionally assign custody to one of the confirmed field workers.';
    }
    if (this.hasMissingActuals()) {
      return 'Enter the actual quantity, date and supporting evidence for each person and equipment allocation.';
    }
    if (this.hasDraftActuals()) return 'Review draft quantities and evidence, then submit them for supervisor approval.';
    if (this.hasSubmittedActuals()) return 'Approval generates the configured cost, payable and billable receivable documents.';
    if (this.status === 'IN_PROGRESS') return 'Return issued resources with incoming condition before completing the package.';
    return 'Approved and reversed records remain locked and traceable to their finance documents.';
  }

  close(): void {
    if (!this.busy()) this.visibleChange.emit(false);
  }

  busy(): boolean {
    return this.loading() || Boolean(this.actionKey());
  }

  assignmentOptions(): Array<FieldLaborAssignment & { label: string }> {
    return (this.currentWork()?.laborAssignments || [])
      .filter(assignment => Boolean(assignment.fieldLaborAssignmentId))
      .map(assignment => ({
        ...assignment,
        label: `${assignment.workerName || assignment.workerNumber || 'Field worker'} — ${this.label(assignment.sourceType)}`
      }));
  }

  entryFor(assignmentId?: number): FieldLaborWorkEntry | undefined {
    return assignmentId ? this.laborEntries().find(entry => entry.fieldLaborAssignmentId === assignmentId
      && !entry.reversalOfWorkEntryId) : undefined;
  }

  usageFor(allocationId?: number): EquipmentUsage | undefined {
    return allocationId ? this.equipmentUsages().find(usage => usage.fieldEquipmentAllocationId === allocationId
      && !usage.reversalOfUsageId) : undefined;
  }

  laborForm(assignment: FieldLaborAssignment): FieldLaborWorkEntry {
    const id = assignment.fieldLaborAssignmentId!;
    return this.laborForms[id] || this.initializeLaborForm(assignment);
  }

  equipmentForm(allocation: FieldEquipmentAllocation): EquipmentUsage {
    const id = allocation.fieldEquipmentAllocationId!;
    return this.equipmentForms[id] || this.initializeEquipmentForm(allocation);
  }

  custodyForm(allocation: FieldEquipmentAllocation): CustodyForm {
    const id = allocation.fieldEquipmentAllocationId!;
    return this.custodyForms[id] || this.initializeCustodyForm(allocation);
  }

  saveLaborActual(assignment: FieldLaborAssignment): void {
    const id = assignment.fieldLaborAssignmentId;
    if (!id) return;
    const form = this.laborForm(assignment);
    const request: FieldLaborWorkEntry = {
      ...form,
      fieldLaborAssignmentId: id,
      workDate: this.dateOnly(this.laborDates[id] || new Date())
    };
    this.run(`labor-save-${id}`, this.service.saveLaborWorkEntry(request), 'Labor actual saved.');
  }

  laborAction(entry: FieldLaborWorkEntry, action: 'submit' | 'approve' | 'reject' | 'reverse'): void {
    if (!entry.fieldLaborWorkEntryId) return;
    const reason = this.decisionReasons[this.decisionKey('labor', entry.fieldLaborWorkEntryId)]?.trim();
    if (['reject', 'reverse'].includes(action) && !reason) {
      this.error.set(`A reason is required to ${action} this labor actual.`);
      return;
    }
    this.run(`labor-${action}-${entry.fieldLaborWorkEntryId}`,
      this.service.laborWorkEntryAction(entry.fieldLaborWorkEntryId, action, { reason }),
      `Labor actual ${this.pastTense(action)}.`);
  }

  packageAction(status: 'IN_PROGRESS' | 'COMPLETED'): void {
    const id = this.currentWork()?.fieldWorkPackageId;
    if (!id) return;
    this.run(`package-${status}`, this.service.transition(id, status),
      status === 'IN_PROGRESS' ? 'Field execution started.' : 'Field work package completed.');
  }

  equipmentAction(allocation: FieldEquipmentAllocation, action: 'issue' | 'start' | 'return'): void {
    const id = allocation.fieldEquipmentAllocationId;
    if (!id) return;
    const form = this.custodyForm(allocation);
    let request: FieldWorkAction = {};
    if (action === 'issue') {
      request = {
        condition: form.issueCondition || 'GOOD',
        custodianLaborAssignmentId: form.custodianLaborAssignmentId
      };
    } else if (action === 'return') {
      request = {
        condition: form.returnCondition || 'GOOD',
        evidenceUrl: form.evidenceUrl,
        damagedQuantity: form.damagedQuantity || 0,
        lostQuantity: form.lostQuantity || 0
      };
      if ((request.damagedQuantity || request.lostQuantity) && !request.evidenceUrl?.trim()) {
        this.error.set('Return evidence is required when equipment is damaged or lost.');
        return;
      }
    }
    this.run(`equipment-${action}-${id}`, this.service.equipmentAction(id, action, request),
      `Equipment ${this.pastTense(action)}.`);
  }

  loadSubstitutionCandidates(allocation: FieldEquipmentAllocation): void {
    const packageId = this.currentWork()?.fieldWorkPackageId;
    const requirementId = allocation.fieldEquipmentRequirementId;
    const allocationId = allocation.fieldEquipmentAllocationId;
    if (!packageId || !requirementId || !allocationId) return;
    this.actionKey.set(`equipment-substitutes-${allocationId}`);
    this.error.set('');
    this.service.equipmentCandidates(packageId, requirementId)
      .pipe(finalize(() => this.actionKey.set('')))
      .subscribe({
        next: candidates => this.substitutionCandidates[allocationId] = candidates.filter(candidate =>
          candidate.equipmentResourceId !== allocation.equipmentResourceId && candidate.available),
        error: error => this.error.set(this.errorMessage(error))
      });
  }

  substituteEquipment(allocation: FieldEquipmentAllocation): void {
    const allocationId = allocation.fieldEquipmentAllocationId;
    if (!allocationId) return;
    const substituteEquipmentResourceId = this.substitutionResourceIds[allocationId];
    const reason = this.substitutionReasons[allocationId]?.trim();
    if (!substituteEquipmentResourceId || !reason) {
      this.error.set('Select an available replacement and enter an audited substitution reason.');
      return;
    }
    this.run(`equipment-substitute-${allocationId}`, this.service.equipmentAction(allocationId, 'substitute', {
      substituteEquipmentResourceId, reason
    }), 'Equipment substituted with a new rate and availability snapshot.');
  }

  saveEquipmentUsage(allocation: FieldEquipmentAllocation): void {
    const id = allocation.fieldEquipmentAllocationId;
    if (!id) return;
    const form = this.equipmentForm(allocation);
    const request: EquipmentUsage = {
      ...form,
      fieldEquipmentAllocationId: id,
      workDate: this.dateOnly(this.equipmentDates[id] || new Date())
    };
    this.run(`usage-save-${id}`, this.service.saveUsage(request), 'Equipment usage saved.');
  }

  usageAction(usage: EquipmentUsage, action: 'submit' | 'approve' | 'reject' | 'reverse'): void {
    if (!usage.fieldEquipmentUsageId) return;
    const reason = this.decisionReasons[this.decisionKey('equipment', usage.fieldEquipmentUsageId)]?.trim();
    if (['reject', 'reverse'].includes(action) && !reason) {
      this.error.set(`A reason is required to ${action} this equipment usage.`);
      return;
    }
    this.run(`usage-${action}-${usage.fieldEquipmentUsageId}`,
      this.service.usageAction(usage.fieldEquipmentUsageId, action, { reason }),
      `Equipment usage ${this.pastTense(action)}.`);
  }

  decisionKey(kind: 'labor' | 'equipment', id?: number): string {
    return `${kind}-${id}`;
  }

  isAction(key: string): boolean {
    return this.actionKey() === key;
  }

  evidenceKey(kind: 'labor' | 'usage' | 'return', id?: number): string {
    return `${kind}-${id || 'new'}`;
  }

  isEvidenceUploading(key: string): boolean {
    return this.evidenceUploading().has(key);
  }

  evidenceFileName(key: string, evidenceUrl?: string): string {
    return this.evidenceFileNames[key] || this.fileNameFromUrl(evidenceUrl) || 'No evidence file selected';
  }

  selectEvidence(event: Event, form: { evidenceUrl?: string }, key: string): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      this.error.set('Evidence files cannot exceed 10 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => this.error.set('The selected evidence file could not be read.');
    reader.onload = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : '';
      const base64Content = dataUrl.includes(',') ? dataUrl.slice(dataUrl.indexOf(',') + 1) : dataUrl;
      if (!base64Content) {
        this.error.set('The selected evidence file could not be read.');
        return;
      }
      this.setEvidenceUploading(key, true);
      this.error.set('');
      this.service.uploadEvidence({
        fileName: file.name,
        mimeType: this.evidenceMimeType(file),
        base64Content
      }).pipe(finalize(() => this.setEvidenceUploading(key, false))).subscribe({
        next: uploaded => {
          form.evidenceUrl = uploaded.fileUrl;
          this.evidenceFileNames[key] = uploaded.fileName || file.name;
          this.message.set('Evidence uploaded. Save the record to attach it.');
        },
        error: error => this.error.set(this.errorMessage(error))
      });
    };
    reader.readAsDataURL(file);
  }

  clearEvidence(form: { evidenceUrl?: string }, key: string): void {
    form.evidenceUrl = '';
    delete this.evidenceFileNames[key];
  }

  hasUnreturnedEquipment(): boolean {
    return (this.currentWork()?.equipmentAllocations || [])
      .some(allocation => ['ISSUED', 'IN_USE'].includes(allocation.allocationStatus || ''));
  }

  canComplete(): boolean {
    return this.status === 'IN_PROGRESS' && !this.hasUnreturnedEquipment();
  }

  severity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (['APPROVED', 'RETURNED', 'COMPLETED', 'IN_USE'].includes(status || '')) return 'success';
    if (['REJECTED', 'CANCELLED', 'REVERSED'].includes(status || '')) return 'danger';
    if (['DRAFT', 'SUBMITTED', 'RESERVED', 'ISSUED', 'RELEASED', 'IN_PROGRESS'].includes(status || '')) return 'warn';
    return 'info';
  }

  private hasUnissuedEquipment(): boolean {
    return (this.currentWork()?.equipmentAllocations || [])
      .some(allocation => allocation.allocationStatus === 'RESERVED');
  }

  private hasMissingActuals(): boolean {
    return (this.currentWork()?.laborAssignments || []).some(
      assignment => !this.entryFor(assignment.fieldLaborAssignmentId))
      || (this.currentWork()?.equipmentAllocations || []).some(
        allocation => ['ISSUED', 'IN_USE', 'RETURNED'].includes(allocation.allocationStatus || '')
          && !this.usageFor(allocation.fieldEquipmentAllocationId));
  }

  private hasDraftActuals(): boolean {
    return this.laborEntries().some(entry => ['DRAFT', 'REJECTED'].includes(entry.entryStatus || ''))
      || this.equipmentUsages().some(usage => ['DRAFT', 'REJECTED'].includes(usage.usageStatus || ''));
  }

  private hasSubmittedActuals(): boolean {
    return this.laborEntries().some(entry => entry.entryStatus === 'SUBMITTED')
      || this.equipmentUsages().some(usage => usage.usageStatus === 'SUBMITTED');
  }

  private run<T>(key: string, request: import('rxjs').Observable<T>, successMessage: string): void {
    this.actionKey.set(key);
    this.error.set('');
    this.message.set('');
    request.pipe(finalize(() => this.actionKey.set(''))).subscribe({
      next: () => {
        this.message.set(successMessage);
        this.refresh();
      },
      error: error => this.error.set(this.errorMessage(error))
    });
  }

  private refresh(): void {
    const packageId = this.currentWork()?.fieldWorkPackageId;
    if (!packageId) return;
    this.loading.set(true);
    this.service.packageById(packageId).subscribe({
      next: work => {
        this.currentWork.set(work);
        this.changed.emit(work);
        const assignmentIds = (work.laborAssignments || [])
          .map(assignment => assignment.fieldLaborAssignmentId).filter((id): id is number => Boolean(id));
        const allocationIds = (work.equipmentAllocations || [])
          .map(allocation => allocation.fieldEquipmentAllocationId).filter((id): id is number => Boolean(id));
        forkJoin({
          labor: assignmentIds.length
            ? forkJoin(assignmentIds.map(id => this.service.laborWorkEntries(id))) : of([] as FieldLaborWorkEntry[][]),
          equipment: allocationIds.length
            ? forkJoin(allocationIds.map(id => this.service.usages(id))) : of([] as EquipmentUsage[][])
        }).pipe(finalize(() => this.loading.set(false))).subscribe({
          next: result => {
            this.laborEntries.set(result.labor.flat());
            this.equipmentUsages.set(result.equipment.flat());
            this.resetForms();
          },
          error: error => this.error.set(this.errorMessage(error))
        });
      },
      error: error => {
        this.loading.set(false);
        this.error.set(this.errorMessage(error));
      }
    });
  }

  private resetForms(): void {
    const today = new Date();
    this.substitutionCandidates = {};
    this.substitutionResourceIds = {};
    this.substitutionReasons = {};
    this.evidenceFileNames = {};
    (this.currentWork()?.laborAssignments || []).forEach(assignment => {
      if (!assignment.fieldLaborAssignmentId) return;
      this.initializeLaborForm(assignment);
      this.laborDates[assignment.fieldLaborAssignmentId] = this.toDate(
        this.entryFor(assignment.fieldLaborAssignmentId)?.workDate) || today;
    });
    (this.currentWork()?.equipmentAllocations || []).forEach(allocation => {
      if (!allocation.fieldEquipmentAllocationId) return;
      this.initializeEquipmentForm(allocation);
      this.initializeCustodyForm(allocation);
      this.equipmentDates[allocation.fieldEquipmentAllocationId] = this.toDate(
        this.usageFor(allocation.fieldEquipmentAllocationId)?.workDate) || today;
    });
  }

  private initializeLaborForm(assignment: FieldLaborAssignment): FieldLaborWorkEntry {
    const id = assignment.fieldLaborAssignmentId!;
    const existing = this.entryFor(id);
    this.laborForms[id] = {
      ...(existing || {}),
      fieldLaborAssignmentId: id,
      actualQuantity: existing?.actualQuantity || assignment.plannedQuantity || 1,
      evidenceUrl: existing?.evidenceUrl || '',
      notes: existing?.notes || ''
    };
    this.rememberEvidenceFile(this.evidenceKey('labor', id), this.laborForms[id].evidenceUrl);
    return this.laborForms[id];
  }

  private initializeEquipmentForm(allocation: FieldEquipmentAllocation): EquipmentUsage {
    const id = allocation.fieldEquipmentAllocationId!;
    const existing = this.usageFor(id);
    this.equipmentForms[id] = {
      ...(existing || {}),
      fieldEquipmentAllocationId: id,
      actualQuantity: existing?.actualQuantity || allocation.plannedQuantity || 1,
      evidenceUrl: existing?.evidenceUrl || '',
      notes: existing?.notes || ''
    };
    this.rememberEvidenceFile(this.evidenceKey('usage', id), this.equipmentForms[id].evidenceUrl);
    return this.equipmentForms[id];
  }

  private initializeCustodyForm(allocation: FieldEquipmentAllocation): CustodyForm {
    const id = allocation.fieldEquipmentAllocationId!;
    this.custodyForms[id] = this.custodyForms[id] || {
      custodianLaborAssignmentId: allocation.custodianLaborAssignmentId,
      issueCondition: allocation.outgoingCondition || 'GOOD',
      returnCondition: allocation.incomingCondition || 'GOOD',
      evidenceUrl: allocation.evidenceUrl || '',
      damagedQuantity: allocation.damagedQuantity || 0,
      lostQuantity: allocation.lostQuantity || 0
    };
    this.rememberEvidenceFile(this.evidenceKey('return', id), this.custodyForms[id].evidenceUrl);
    return this.custodyForms[id];
  }

  private dateOnly(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toDate(value?: string | Date): Date | undefined {
    if (!value) return undefined;
    const date = value instanceof Date ? value : new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  private pastTense(action: string): string {
    return ({ submit: 'submitted', approve: 'approved', reject: 'rejected', reverse: 'reversed',
      issue: 'issued', start: 'started', return: 'returned' } as Record<string, string>)[action] || action;
  }

  private label(value?: string): string {
    return (value || 'Unspecified').replaceAll('_', ' ').toLowerCase()
      .replace(/\b\w/g, character => character.toUpperCase());
  }

  private setEvidenceUploading(key: string, uploading: boolean): void {
    this.evidenceUploading.update(current => {
      const next = new Set(current);
      if (uploading) next.add(key); else next.delete(key);
      return next;
    });
  }

  private rememberEvidenceFile(key: string, evidenceUrl?: string): void {
    const fileName = this.fileNameFromUrl(evidenceUrl);
    if (fileName) this.evidenceFileNames[key] = fileName;
  }

  private fileNameFromUrl(value?: string): string | undefined {
    if (!value) return undefined;
    try {
      const decoded = decodeURIComponent(value);
      const segment = decoded.split('?')[0].split('/').pop() || '';
      return segment.replace(/^evidence_\d+_[^_]+_/, '') || undefined;
    } catch {
      return undefined;
    }
  }

  private evidenceMimeType(file: File): string {
    if (file.type) return file.type;
    const extension = file.name.split('.').pop()?.toLowerCase();
    return ({
      pdf: 'application/pdf', doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      txt: 'text/plain'
    } as Record<string, string>)[extension || ''] || 'application/octet-stream';
  }

  private errorMessage(error: any): string {
    return error?.error?.message || error?.error?.error || error?.message || 'Field execution action failed.';
  }
}
